/**
 * Buy Now with Kite Gift Support
 *
 * Workflow:
 * 1. Save current cart items to localStorage
 * 2. Clear cart completely
 * 3. Add current product to empty cart
 * 4. Trigger Kite App to add free gifts
 * 5. Redirect to /checkout (with ONLY product + gifts)
 * 6. Cart will be restored automatically when user returns (via cart-restore.js)
 *
 * This ensures ONLY current product + gifts go to checkout,
 * while preserving user's original cart for later restoration.
 *
 * Status messages:
 * - "Comprar ahora" (default)
 * - "Adding to cart..." (steps 1-3)
 * - "Preparing your free gifts..." (step 4, waiting for Kite)
 * - "Redirecting to checkout..." (step 5)
 */

class BuyNowWithGift {
  constructor() {
    this.kiteWaitTime = 1000; // 1 second - reduced for better performance
    this.maxCheckAttempts = 3; // Check cart multiple times
    this.checkInterval = 300; // Check every 300ms
    this.init();
  }

  init() {
    // Use event delegation to handle dynamically loaded buttons
    // This works for buttons in quick view / product drawer
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.buy-now-button');
      if (button) {
        event.preventDefault();
        event.stopPropagation();
        this.handleBuyNow({ currentTarget: button });
      }
    });

    console.log('[Buy Now] Initialized with event delegation (supports dynamic content)');
  }

  async handleBuyNow(event) {
    const button = event.currentTarget;
    const formId = button.dataset.formId;
    const form = document.getElementById(formId);

    if (!form) {
      console.error('[Buy Now] Form not found:', formId);
      return;
    }

    // Prevent double-click
    if (button.disabled) return;

    try {
      console.log('[Buy Now] Starting process...');

      // ============================================
      // STEP 1: Save Current Cart to localStorage
      // ============================================
      this.updateButtonState(button, 'adding');

      const currentCartResponse = await fetch('/cart.js');
      const currentCart = await currentCartResponse.json();

      if (currentCart.item_count > 0) {
        // Save to localStorage with timestamp
        localStorage.setItem('iegeek_saved_cart', JSON.stringify(currentCart.items));
        localStorage.setItem('iegeek_saved_cart_time', Date.now().toString());
        console.log('[Buy Now] Saved', currentCart.item_count, 'items to localStorage');
      }

      // ============================================
      // STEP 2: Clear Cart
      // ============================================
      console.log('[Buy Now] Clearing cart...');
      await fetch('/cart/clear.js', { method: 'POST' });
      console.log('[Buy Now] ✓ Cart cleared');

      // ============================================
      // STEP 3: Add Current Product
      // ============================================
      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = formData.get('quantity');

      console.log('[Buy Now] Adding product to empty cart:', {
        variantId: variantId,
        quantity: quantity
      });

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: parseInt(quantity)
        })
      });

      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const addedItem = await addResponse.json();
      console.log('[Buy Now] ✓ Product added:', addedItem.product_title);

      // ============================================
      // STEP 4: Trigger Kite App to Add Gifts
      // ============================================
      this.updateButtonState(button, 'preparing');

      // Refresh cart to trigger Kite App
      console.log('[Buy Now] Triggering Kite App...');
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      console.log('[Buy Now] Cart after product added:', cartData.item_count, 'items');

      // Dispatch custom event for Kite App
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cartData }
      }));

      // Also try window event
      if (window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: cartData }
        }));
      }

      // Wait for Kite App to process and add gifts
      console.log(`[Buy Now] Waiting for Kite to add gifts...`);

      // Smart wait: Check cart multiple times instead of waiting fixed time
      let itemCount = cartData.item_count;
      let attempts = 0;

      while (attempts < this.maxCheckAttempts) {
        await this.wait(this.checkInterval);

        const checkResponse = await fetch('/cart.js');
        const checkCart = await checkResponse.json();

        // If item count increased, gifts were added
        if (checkCart.item_count > itemCount) {
          console.log(`[Buy Now] ✓ Gifts detected after ${(attempts + 1) * this.checkInterval}ms`);
          itemCount = checkCart.item_count;
          break;
        }

        attempts++;
      }

      // Final wait to ensure Kite is done
      if (attempts === this.maxCheckAttempts) {
        console.log(`[Buy Now] No additional gifts detected, proceeding...`);
      }

      await this.wait(200); // Small buffer

      // ============================================
      // STEP 5: Redirect to Checkout
      // ============================================
      this.updateButtonState(button, 'redirecting');

      // Verify cart has items
      const finalCartResponse = await fetch('/cart.js');
      const finalCart = await finalCartResponse.json();
      console.log('[Buy Now] Final cart:', finalCart.item_count, 'items (product + gifts)');

      if (finalCart.item_count === 0) {
        throw new Error('No items in cart to checkout');
      }

      console.log('[Buy Now] Redirecting to checkout...');
      console.log('[Buy Now] Cart will be restored automatically when you return');
      await this.wait(300);

      // Redirect to checkout (cart contains ONLY product + gifts)
      window.location.href = '/checkout';

    } catch (error) {
      console.error('[Buy Now] Error:', error);

      // Show user-friendly error
      this.showError(button, error.message || 'Something went wrong. Please try again.');

      // Reset button after 3 seconds
      setTimeout(() => {
        this.updateButtonState(button, 'default');
      }, 3000);
    }
  }

  /**
   * Update button visual state
   * @param {HTMLElement} button
   * @param {string} state - 'default', 'adding', 'preparing', 'redirecting'
   */
  updateButtonState(button, state) {
    // Hide all state texts
    const states = ['default', 'adding', 'preparing', 'redirecting'];
    states.forEach(s => {
      const element = button.querySelector(`.buy-now-button--${s}`);
      if (element) {
        element.style.display = 'none';
      }
    });

    // Show loading spinner for non-default states
    const loadingSpinner = button.querySelector('.loading-overlay');
    if (loadingSpinner) {
      loadingSpinner.style.display = state !== 'default' ? 'block' : 'none';
    }

    // Show current state text
    const currentStateElement = button.querySelector(`.buy-now-button--${state}`);
    if (currentStateElement) {
      currentStateElement.style.display = 'block';
    }

    // Disable button during process
    button.disabled = state !== 'default';

    console.log('[Buy Now] Button state:', state);
  }

  /**
   * Show error message
   */
  showError(button, message) {
    // Try to find existing error container
    let errorContainer = button.parentElement.querySelector('.buy-now-error');

    if (!errorContainer) {
      errorContainer = document.createElement('div');
      errorContainer.className = 'buy-now-error form-notification error';
      errorContainer.style.cssText = 'margin-top: 10px; padding: 10px; background: #fee; border: 1px solid #fcc; border-radius: 4px; color: #c00;';
      button.parentElement.appendChild(errorContainer);
    }

    errorContainer.textContent = message || 'Something went wrong. Please try again.';
    errorContainer.style.display = 'block';

    // Hide error after 5 seconds
    setTimeout(() => {
      if (errorContainer) {
        errorContainer.style.display = 'none';
      }
    }, 5000);
  }

  /**
   * Utility: Wait/delay
   */
  wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BuyNowWithGift();
  });
} else {
  new BuyNowWithGift();
}
